import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Loader2, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { PERKS } from "../mock";
import { CrownIcon } from "./Icons";
import { memberApi, setMemberToken, errMsg } from "../lib/api";
import RazorpayButton from "./RazorpayButton";

export default function VipCheckout({ open, onOpenChange, initialTab = "join" }) {
  const nav = useNavigate();
  const [tab, setTab] = useState(initialTab);
  const [step, setStep] = useState("form"); // "form" | "pay"
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [login, setLogin] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (open) { setTab(initialTab); setStep("form"); } }, [open, initialTab]);

  // Once signup is done we're in "pay" step - poll until the Razorpay webhook activates the account.
  useEffect(() => {
    if (step !== "pay") return;
    const t = setInterval(async () => {
      try {
        const { data } = await memberApi.get("/member/me");
        if (data.active) { clearInterval(t); toast.success("Payment received. Welcome to Maya VIP!"); onOpenChange(false); nav("/members/chat"); }
      } catch { /* not logged in yet or transient error, keep polling */ }
    }, 4000);
    return () => clearInterval(t);
  }, [step]); // eslint-disable-line

  const done = () => { onOpenChange(false); nav("/members/chat"); };

  const join = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || f.password.length < 6) return toast.error("Enter name, email and a 6+ character password");
    setLoading(true);
    try {
      const { data } = await memberApi.post("/member/signup", { name: f.name, email: f.email, password: f.password });
      setMemberToken(data.token);
      setStep("pay");
    } catch (err) { toast.error(errMsg(err, "Signup failed")); }
    finally { setLoading(false); }
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { const { data } = await memberApi.post("/member/login", login); setMemberToken(data.token); toast.success("Welcome back!"); done(); }
    catch (err) { toast.error(errMsg(err, "Login failed")); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[480px] rounded-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-full bg-[#ffe6ee] grid place-items-center"><CrownIcon className="w-6 h-6 text-[#ee3a70]" /></span>
          <div>
            <DialogTitle className="text-[22px]">Maya VIP</DialogTitle>
            <DialogDescription>₹499/month · cancel anytime</DialogDescription>
          </div>
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="join" data-testid="vip-tab-join">Join VIP</TabsTrigger>
            <TabsTrigger value="login" data-testid="vip-tab-login">I'm already a member</TabsTrigger>
          </TabsList>

          <TabsContent value="join">
            {step === "form" ? (
              <>
                <ul className="grid grid-cols-2 gap-x-3 gap-y-1.5 mt-3">
                  {PERKS.map((p) => (
                    <li key={p.text} className="flex items-start gap-1.5 text-[12.5px] text-[#3a3336]"><Check className="w-3.5 h-3.5 mt-0.5 text-[#ee3a70] shrink-0" />{p.text}</li>
                  ))}
                </ul>
                <form onSubmit={join} className="mt-4 space-y-3" data-testid="vip-checkout-form">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label htmlFor="vn">Name</Label><Input id="vn" data-testid="vip-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Your name" /></div>
                    <div><Label htmlFor="ve">Email</Label><Input id="ve" type="email" data-testid="vip-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="you@email.com" /></div>
                  </div>
                  <div><Label htmlFor="vp">Create password</Label><Input id="vp" type="password" data-testid="vip-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="6+ characters" /></div>
                  <button type="submit" disabled={loading} className="btn-vip-big !h-[52px] !text-[17px]" data-testid="vip-subscribe">
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Continue to payment <ChevronRight className="w-5 h-5" /></>}
                  </button>
                </form>
              </>
            ) : (
              <div className="mt-4 space-y-4" data-testid="vip-pay-step">
                <p className="text-[14px] text-[#4a4246]">Almost there, <span className="font-semibold">{f.name}</span>! Complete your ₹499 payment below to unlock Maya VIP.</p>
                <div className="rounded-xl border border-[#f0e4e8] p-4 bg-[#fffafb] grid place-items-center">
                  <RazorpayButton email={f.email} name={f.name} />
                </div>
                <div className="flex items-center justify-center gap-2 text-[12px] text-[#8a8286]">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Your VIP unlocks automatically a few seconds after payment.
                </div>
                <button type="button" onClick={done} className="w-full text-center text-[13px] text-[#8a8286] underline" data-testid="vip-pay-later">I'll pay later</button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="login">
            <form onSubmit={doLogin} className="mt-4 space-y-3" data-testid="vip-login-form">
              <div><Label>Email</Label><Input type="email" data-testid="login-email" value={login.email} onChange={(e) => setLogin({ ...login, email: e.target.value })} /></div>
              <div><Label>Password</Label><Input type="password" data-testid="login-password" value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })} /></div>
              <button type="submit" disabled={loading} className="btn-vip-big !h-[50px] !text-[16px]" data-testid="login-submit">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Log in to my space"}
              </button>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

